import { create } from "zustand";

/**
 * The Document Picture-in-Picture API, which TypeScript's DOM library does not yet declare.
 * Only the members used here are typed.
 */
interface DocumentPictureInPicture {
	readonly window: Window | null;
	requestWindow(options?: {
		width?: number;
		height?: number;
		/** Hides the browser's "back to tab" button, which otherwise closes the window. */
		disallowReturnToOpener?: boolean;
		/** Opens where we ask rather than where the user last dragged it. */
		preferInitialWindowPlacement?: boolean;
	}): Promise<Window>;
}

function api(): DocumentPictureInPicture | null {
	if (typeof window === "undefined") return null;
	return (
		(window as unknown as { documentPictureInPicture?: DocumentPictureInPicture })
			.documentPictureInPicture ?? null
	);
}

/** Whether this browser can float a window of our own above other applications. */
export function isPipSupported() {
	return api() !== null;
}

interface PipWindowState {
	/** The floating window, while one is open. */
	pipWindow: Window | null;
}

/**
 * The floating window, published so React can portal into it. A `Window` is not serialisable
 * state, but it is exactly the handle the panel needs and it lives and dies with one recording.
 */
export const usePipWindowStore = create<PipWindowState>()(() => ({ pipWindow: null }));

/** Torn down together with the window: the observers keeping it looking like the opener. */
let observers: MutationObserver[] = [];

/** The one keeping it the size of its contents. */
let sizeObserver: { disconnect(): void } | null = null;

/**
 * Mirror the opener's stylesheets into the floating window.
 *
 * A picture-in-picture document starts empty — it inherits no CSS at all — so without this the
 * panel arrives unstyled. Same-origin sheets are serialised rule by rule; a sheet whose rules
 * cannot be read (a cross-origin one) is re-linked by URL instead.
 */
function copyStyles(target: Window) {
	for (const previous of target.document.querySelectorAll("[data-copied-style]")) {
		previous.remove();
	}
	for (const sheet of Array.from(document.styleSheets)) {
		try {
			const style = target.document.createElement("style");
			style.dataset.copiedStyle = "";
			style.textContent = Array.from(sheet.cssRules)
				.map((rule) => rule.cssText)
				.join("");
			target.document.head.append(style);
		} catch {
			if (!sheet.href) continue;
			const link = target.document.createElement("link");
			link.dataset.copiedStyle = "";
			link.rel = "stylesheet";
			link.href = sheet.href;
			target.document.head.append(link);
		}
	}
}

/** Carry the opener's theme across — dark mode is a `.dark` ancestor class, not a media query. */
function copyClasses(target: Window) {
	target.document.documentElement.className = document.documentElement.className;
	target.document.body.className = document.body.className;
	target.document.body.style.margin = "0";
	// The document fills the window at whatever size it ends up, so the panel's own surface reaches
	// every edge. A window the author drags larger then grows the panel's background with it rather
	// than exposing bare document in the corner.
	target.document.documentElement.style.height = "100%";
	target.document.body.style.height = "100%";
}

/**
 * Keep the window the size of the panel inside it.
 *
 * The requested size is a guess made before the panel exists, and it is wrong twice over: the
 * browser adds a title bar the request does not account for, and the surface changes shape during
 * one recording — the panel while it runs, the taller card while the upload finishes.
 *
 * It measures the panel and not the window, which is what separates the two things the author can
 * do. The surface changing size moves the window to fit it. The author dragging the window does
 * not: the panel is the same size as it was, nothing fires, and the window stays where they put
 * it — with the document filling it, so there is nothing bare to see at the new size.
 *
 * The element is found rather than passed because React portals it in after this runs, and it is
 * looked up again on every change so a surface that remounts is still the one being watched.
 */
function fitWindowToContent(target: Window) {
	// Typed rather than narrowed with `instanceof`: the element belongs to the floating window's
	// realm, where the opener's `HTMLElement` is a different constructor and the check is false.
	let observed: HTMLElement | null = null;

	function fit() {
		if (!observed) return;
		const dx = observed.offsetWidth - target.innerWidth;
		const dy = observed.offsetHeight - target.innerHeight;
		// A pixel either way is the browser rounding, not a gap worth a resize — and resizing on
		// it would trade the gap for a window that twitches.
		if (Math.abs(dx) <= 1 && Math.abs(dy) <= 1) return;
		try {
			target.resizeBy(dx, dy);
		} catch (error) {
			console.error("Resizing the floating recording window failed:", error);
		}
	}

	const resizeObserver = new ResizeObserver(fit);
	function attach() {
		const content = target.document.querySelector<HTMLElement>("[data-pip-content]");
		if (!content || content === observed) return;
		if (observed) resizeObserver.unobserve(observed);
		observed = content;
		resizeObserver.observe(content);
	}

	const arrivalObserver = new MutationObserver(attach);
	arrivalObserver.observe(target.document.body, { childList: true, subtree: true });
	attach();

	return {
		disconnect() {
			resizeObserver.disconnect();
			arrivalObserver.disconnect();
		},
	};
}

/**
 * Open the floating control window, or answer null when this browser has no such thing.
 *
 * **Call this straight from a click.** Opening such a window spends transient user activation,
 * which is why it is the author's own act rather than something the recorder does on their
 * behalf: a window that appeared at every start would be in the way of everyone who never
 * leaves the browser, and it can only be opened while a gesture is fresh.
 *
 * Never throws for the caller's purposes: a browser without the API, a refused window or a
 * second call while one is already open all answer null, and the panel then stays in the page.
 */
export async function openPipWindow() {
	const pip = api();
	if (!pip) return null;
	if (pip.window) return pip.window;

	let target: Window;
	try {
		target = await pip.requestWindow({
			// Sized to the panel rather than to a comfortable minimum: a window much larger than
			// its content reads as a broken dialog, and this one has no chrome to fill the gap.
			// The browser's "back to tab" button is deliberately left in place — with the panel
			// undocked by hand, that button is how it docks again.
			width: 330,
			height: 120,
			preferInitialWindowPlacement: true,
		});
	} catch (error) {
		console.error("Opening the floating recording window failed:", error);
		return null;
	}

	copyStyles(target);
	copyClasses(target);

	// The opener injects stylesheets after load — a lazily loaded chunk in production, the dev
	// server's own on every hot update — and the copy above is a snapshot, so it has to be redone
	// when that happens. The theme can change at any time too.
	const fitObserver = fitWindowToContent(target);
	const styleObserver = new MutationObserver(() => copyStyles(target));
	styleObserver.observe(document.head, { childList: true });
	const themeObserver = new MutationObserver(() => copyClasses(target));
	themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
	themeObserver.observe(document.body, { attributes: true, attributeFilter: ["class"] });
	observers = [styleObserver, themeObserver];
	sizeObserver = fitObserver;

	// `pagehide` is the single teardown hook: it fires whether we closed the window or the author
	// did. Losing it is not losing the recording — the panel simply reappears inside the page.
	target.addEventListener("pagehide", () => {
		for (const observer of observers) observer.disconnect();
		observers = [];
		sizeObserver?.disconnect();
		sizeObserver = null;
		usePipWindowStore.setState({ pipWindow: null });
	});

	usePipWindowStore.setState({ pipWindow: target });
	return target;
}

/** Close the floating window. The `pagehide` handler above does the rest. */
export function closePipWindow() {
	usePipWindowStore.getState().pipWindow?.close();
}
